import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import { Globe } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const { language, toggleLanguage } = useLanguage();
  const t = translations[language];
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');

  useEffect(() => {
    const sectionIds = ['hero', 'about', 'services', 'skills', 'experience', 'projects', 'contact'];
    const sections = sectionIds
      .map((id) => document.getElementById(id))
      .filter(Boolean);

    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => (b.intersectionRatio ?? 0) - (a.intersectionRatio ?? 0));

        if (visible[0]?.target?.id) {
          setActiveSection(visible[0].target.id);
        }
      },
      {
        root: null,
        threshold: [0.2, 0.35, 0.5, 0.65],
        rootMargin: '-30% 0px -55% 0px'
      }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
    }
  };

  const closeMenu = () => setIsMobileMenuOpen(false);

  return (
    <header
      className={
        'fixed top-0 left-0 right-0 z-50 bg-background border-b border-border transition-colors duration-100 ease-linear'
      }
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => scrollToSection('hero')}
            className="text-sm md:text-base font-mono tracking-[0.35em] text-foreground hover:opacity-80 transition-opacity duration-100 ease-linear"
            aria-label="Back to top"
          >
            PS
          </button>

          <div className="flex items-center space-x-4">
            <Button
              onClick={toggleLanguage}
              variant="outline"
              size="sm"
              className="flex items-center space-x-2"
              aria-label="Toggle language"
            >
              <Globe className="w-4 h-4" />
              <span className="font-medium">{language.toUpperCase()}</span>
            </Button>

            <Button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              variant={isMobileMenuOpen ? 'default' : 'outline'}
              size="sm"
              className="tracking-[0.35em]"
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-nav"
            >
              {isMobileMenuOpen ? 'CLOSE' : 'MENU'}
            </Button>
          </div>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div
          id="mobile-nav"
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[60] bg-background text-foreground overflow-y-auto"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeMenu();
          }}
        >
          <div
            className="max-w-6xl mx-auto px-4 sm:px-6 pt-24 pb-10"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-4">
              <button
                onClick={() => scrollToSection('hero')}
                className="text-sm md:text-base font-mono tracking-[0.35em] text-foreground hover:opacity-80 transition-opacity duration-100 ease-linear"
                aria-label="Back to top"
              >
                PS
              </button>
              <button
                onClick={closeMenu}
                className="h-9 px-3 border border-foreground font-mono text-xs tracking-[0.35em] hover:bg-foreground hover:text-background transition-colors duration-100 ease-linear focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                CLOSE
              </button>
            </div>
            <div className="mt-4 h-px bg-border"></div>

            <nav className="mt-8 flex flex-col" aria-label="Primary">
              {[
                { id: 'about', label: t.nav.about },
                { id: 'services', label: t.nav.services },
                { id: 'skills', label: t.nav.skills },
                { id: 'experience', label: t.nav.experience },
                { id: 'projects', label: t.nav.projects },
                { id: 'contact', label: t.nav.contact }
              ].map((item) => {
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={
                      'py-4 text-left text-4xl sm:text-5xl md:text-6xl font-mono tracking-tight transition-colors duration-100 ease-linear ' +
                      (isActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground') +
                      ' focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background'
                    }
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <span className="inline-flex items-center gap-4">
                      <span
                        className={
                          'h-2 w-2 rounded-full border border-foreground ' +
                          (isActive ? 'bg-foreground' : 'bg-background')
                        }
                      />
                      <span>{item.label}</span>
                    </span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
